import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import { useSelector, useDispatch } from 'react-redux';
import './style.css';
import {
  EDITOR_TAB_PANEL_INSPECTOR,
  VERTICAL_ORIENTATION
} from '../../../../utilities/constants/editorConstants';
import {
  selectSingleNode,
  setCurrentNodeId,
  toggleNodeSelection,
  updateNodeAttributes
} from '../../../../redux/reducers/global/node/nodeSlice';
import { setTabPanel } from '../../../../redux/reducers/global/editor/editorSlice';
import { setScene } from '../../../../redux/reducers/global/inspector/inspectorSlice';
import { useNodeOperations } from '../../../../hooks/useNodeOperations';
import {
  THREE_WINDING_BASE_ALIAS,
  SWITCH_TRANSFER_BASE_ALIAS,
  resolveThreeWindingHandleFraction,
  resolveThreeWindingImage,
  resolveSwitchTransferHandleFraction,
  resolveSwitchTransferImage,
  resolveSwitchImage,
  resolveInterruptorMtAtAImage,
  resolveUtilityBImage,
  INTERRUPTOR_MT_AT_A_ALIAS,
  SWITCH_ALIAS,
  UTILITY_B_ALIAS
} from './scaleAwareSymbolUtils';

const MAX_LABEL_DISTANCE = 600;

const toHandleClassToken = (value, fallback = 'default') => {
  const text = typeof value === 'string' && value.length > 0 ? value : fallback;
  return text.replace(/[^a-zA-Z0-9_-]/g, '_');
};

const normalizeTintHex = (value) => {
  if (typeof value !== 'string') return null;
  let hex = value.trim();
  if (!hex) return null;
  if (!hex.startsWith('#')) {
    hex = `#${hex}`;
  }
  if (/^#([0-9a-fA-F]{3})$/.test(hex)) {
    hex = `#${hex
      .slice(1)
      .split('')
      .map((ch) => ch + ch)
      .join('')}`;
  }
  if (!/^#([0-9a-fA-F]{6})$/.test(hex)) return null;
  return hex.toLowerCase();
};

const hexChannel = (hex, start) => parseInt(hex.slice(start, start + 2), 16);

const hexToRgba = (hex, alpha = 1) => {
  const normalized = normalizeTintHex(hex);
  if (!normalized) return null;
  const r = hexChannel(normalized, 1);
  const g = hexChannel(normalized, 3);
  const b = hexChannel(normalized, 5);
  const clampedAlpha = Math.max(0, Math.min(1, alpha));
  return `rgba(${r}, ${g}, ${b}, ${clampedAlpha})`;
};

const stripDiacritics = (value = '') =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const normalizeText = (value) =>
  typeof value === 'string' ? stripDiacritics(value).trim().toLowerCase() : '';

const UNIT_SUFFIX_BY_KEY = {
  ratedVoltage: 'V',
  voltage: 'V',
  primaryVoltage: 'V',
  secondaryVoltage: 'V',
  tertiaryVoltage: 'V',
  voltageCurrent: 'V',
  ratedPower: 'kVA',
  power: 'kVA',
  ratedCurrent: 'A',
  shortCircuitCurrent: 'A',
  length: 'm',
  breakingCapacity: 'kA',
  protectionBreakingCapacity: 'kA',
  efficiency: '%',
  threePhaseLines: 'A',
  lineasTrifasicas: 'A',
  threePhaseIcc: 'A',
  lineToGroundIcc: 'A',
  threePhaseICC: 'A',
  lineToGroundICC: 'A',
  ampacity: 'A',
  ampacidad: 'A',
  impedance: '%',
  neutralImpedance: 'Ohm',
};

const CURRENT_UNIT_KEYS = new Set([
  'ratedCurrent',
  'shortCircuitCurrent',
  'threePhaseLines',
  'lineasTrifasicas',
  'threePhaseIcc',
  'lineToGroundIcc',
  'threePhaseICC',
  'lineToGroundICC',
  'ampacity',
  'ampacidad',
]);

const VOLTAGE_UNIT_KEYS = new Set([
  'ratedVoltage',
  'voltage',
  'primaryVoltage',
  'secondaryVoltage',
  'tertiaryVoltage',
  'voltageCurrent',
]);

const POWER_UNIT_KEYS = new Set([
  'ratedPower',
  'power',
]);

const HIDDEN_CANVAS_ATTRIBUTES_BY_SYMBOL = {
  DistributionBoard: new Set([
    'headerData',
    'rowData',
    'columnWidths',
    'showDistributionBoardPreview',
    'distributionBoardPreviewScale',
    'distributionBoardPreviewPosition',
  ]),
};

const resolveUnitSuffix = (fieldKey, fieldLabel) => {
  if (fieldKey && UNIT_SUFFIX_BY_KEY[fieldKey]) {
    return UNIT_SUFFIX_BY_KEY[fieldKey];
  }
  const normalized = normalizeText(fieldLabel);
  if (!normalized) return null;
  if (normalized.includes('voltaje nominal') || normalized === 'voltaje' || normalized.startsWith('voltaje ')) {
    return 'V';
  }
  if (normalized.includes('potencia nominal')) {
    return 'kVA';
  }
  if (normalized.includes('eficiencia')) {
    return '%';
  }
  if (normalized.includes('corriente nominal')) {
    return 'A';
  }
  if (normalized.includes('corriente de corto circuito') || normalized.includes('corto circuito')) {
    return 'A';
  }
  if (normalized.includes('icc trifasica') || normalized.includes('icc de linea a tierra')) {
    return 'A';
  }
  if (normalized === 'longitud') {
    return 'm';
  }
  if (normalized.includes('capacidad interruptiva')) {
    return 'kA';
  }
  if (normalized.includes('lineas trifasicas')) {
    return 'A';
  }
  if (normalized.includes('impedancia de neutro')) {
    return 'Ohm';
  }
  if (normalized.startsWith('impedancia') || normalized.includes('impedancia')) {
    return '%';
  }
  return null;
};

const formatAttributeValue = (key, label, attribute, symbolLabel) => {
  if (!attribute) return '';
  const rawValue = attribute.value;
  if (rawValue === undefined || rawValue === null) return '';
  const rawText = typeof rawValue === 'string' ? rawValue : String(rawValue);
  const hasTrailingSpace = /\s$/.test(rawText);
  const text = rawText.trim();
  if (!text) return '';
  const disableCurrentUnitToggle = symbolLabel === 'Fuse';
  const explicitUnit = (() => {
    if (key === 'phaseGauge' || key === 'neutralGauge' || key === 'groundGauge') {
      const allowed = ['AWG', 'KCMIL', 'mm2'];
      return allowed.includes(attribute.unitSuffix) ? attribute.unitSuffix : 'KCMIL';
    }
    if (POWER_UNIT_KEYS.has(key)) {
      const allowed = ['HP', 'kW', 'kVA'];
      return allowed.includes(attribute.unitSuffix) ? attribute.unitSuffix : 'kVA';
    }
    if (VOLTAGE_UNIT_KEYS.has(key)) {
      const allowed = ['V', 'kV'];
      return allowed.includes(attribute.unitSuffix) ? attribute.unitSuffix : 'V';
    }
    if (CURRENT_UNIT_KEYS.has(key)) {
      if (disableCurrentUnitToggle) {
        return 'A';
      }
      const allowed = ['A', 'kA'];
      return allowed.includes(attribute.unitSuffix) ? attribute.unitSuffix : 'A';
    }
    if (key === 'std' || key === 'gfd') {
      const unit = attribute.unitSuffix === 'I2t OFF' ? 'I²t OFF' : 'I²t ON';
      return unit;
    }
    if (key === 'length') {
      const allowed = ['m', 'km', 'pies'];
      return allowed.includes(attribute.unitSuffix) ? attribute.unitSuffix : 'm';
    }
    return attribute.unitSuffix;
  })();
  if (explicitUnit) {
    return `${text} ${explicitUnit}`.trim();
  }
  const suffix = resolveUnitSuffix(key, label);
  if (!suffix) return text;
  const joiner = hasTrailingSpace ? ' ' : '';
  return `${text}${joiner}${suffix}`;
};

const CustomNode = ({ id, data, selected }) => {
  const dispatch = useDispatch();
  const reactFlowInstance = useReactFlow();
  const { setNodes } = useNodeOperations();

  // 🛠 Debug Redux State
  const nodes = useSelector(state => state.node.nodes) || [];
  const edges = useSelector(state => state.edge.edges) || [];
//  console.log("🛠 Redux Nodes:", nodes);

  const currentNode = nodes.find(node => node.id === id);
//  console.log("🔍 Current Node:", currentNode);

  const attributes = currentNode?.data?.attributes || {};
  const hideAttributeLabelsDefault = useSelector((state) => !!state.view.hideParameterLabelsDefault);
  const attributeLabelSeparator = useSelector((state) => state.view.attributeLabelSeparator ?? ':');
  const hideAttributeLabels = typeof currentNode?.data?.hideAttributeLabels === 'boolean'
    ? currentNode.data.hideAttributeLabels
    : hideAttributeLabelsDefault;
  const attributeRows = [];
  Object.entries(attributes)
    .filter(([_, attr]) => {
      if (!attr || !attr.isVisible) return false;
      const raw = attr.value;
      if (raw === undefined || raw === null) return false;
      const text = typeof raw === 'string' ? raw.trim() : String(raw).trim();
      return text.length > 0;
    })
    .filter(([key]) => {
      const hiddenKeys = HIDDEN_CANVAS_ATTRIBUTES_BY_SYMBOL[data?.symbolLabel];
      return !(hiddenKeys && hiddenKeys.has(key));
    })
    .sort(([keyA], [keyB]) => {
      if (keyA === 'nombre' && keyB !== 'nombre') return -1;
      if (keyB === 'nombre' && keyA !== 'nombre') return 1;
      return 0;
    })
    .forEach(([key, attr]) => {
      const rawLabel = attr.label;
      const label = rawLabel ?? key;
      const labelText = typeof rawLabel === 'string' && rawLabel.trim() === '' ? '' : label;
      const formatLabel = labelText || key;
      const displayValue = formatAttributeValue(key, formatLabel, attr, data?.symbolLabel);
      const rowClassName = hideAttributeLabels ? 'attr-row attr-row--labels-hidden' : 'attr-row';
      if (key === 'phaseLongTimePickup') {
        attributeRows.push(
          <div key="phase-block-label" className="attr-row attr-row--group">
            <div className="attr-row__group-label">Fase</div>
          </div>
        );
      }
      if (key === 'groundLongTimePickup') {
        attributeRows.push(
          <div key="ground-block-label" className="attr-row attr-row--group">
            <div className="attr-row__group-label">Tierra</div>
          </div>
        );
      }
      const separatorText = typeof attributeLabelSeparator === 'string' ? attributeLabelSeparator : ':';
      const shouldShowSeparator = !hideAttributeLabels && labelText && separatorText.length > 0;
      const labelWithSeparator = shouldShowSeparator ? `${labelText}${separatorText}` : labelText;
      attributeRows.push(
        <div key={key} className={rowClassName} style={{ lineHeight: 1.25 }}>
          {!hideAttributeLabels && labelText ? <div className="attr-row__label bold">{labelWithSeparator}</div> : null}
          <div className="attr-row__value">{displayValue}</div>
        </div>
      );
    });

  const isSelected = selected; // use React Flow selection for styling
  const tintColor = useMemo(() => normalizeTintHex(data?.tintColor), [data?.tintColor]);
  const selectionAccent = tintColor || '#2cabef';
  const selectionBackground = useMemo(() => hexToRgba(selectionAccent, 0.12) || 'rgba(44, 171, 239, 0.12)', [selectionAccent]);
  const wrapperStyle = useMemo(() => {
    const base = {
      position: 'relative',
      '--node-selection-bg': selectionBackground,
    };
    if (tintColor) {
      base['--node-tint'] = tintColor;
    }
    if (isSelected) {
      base.borderColor = selectionAccent;
      base.background = selectionBackground;
    }
    return base;
  }, [tintColor, isSelected, selectionAccent, selectionBackground]);
  const globalScaleCm = useSelector((state) => state.view.scaleCm);
  const globalLabelFontPx = useSelector((state) => state.view.labelFontPx) || 38;
  const labelFontPx = typeof data?.textSizePx === 'number' && data.textSizePx > 0 ? data.textSizePx : globalLabelFontPx;
  const labelPositionEditNodeId = useSelector((state) => state.view.labelPositionEditNodeId);
  const distributionBoardPreviewEditNodeId = useSelector((state) => state.view.distributionBoardPreviewEditNodeId);
  const isLabelPositionEditing = labelPositionEditNodeId === id;
  const isDistributionBoardPreviewEditing = distributionBoardPreviewEditNodeId === id;
  const BASE_PX = 100;

  // selection and inspector updates are handled by Diagram onSelectionChange

  const [dimensions, setDimensions] = useState({ width: 100, height: 100 });
  const gridGapPx = useSelector((state) => state.view.gridGapPx) || 25;
  const symbolFootprintPx = Math.max(5, Math.min(200, Number(gridGapPx) || 25)) * 4;
  const scaleFactor = symbolFootprintPx / BASE_PX;
  const [isLabelDragging, setIsLabelDragging] = useState(false);
  const [labelPositionDraft, setLabelPositionDraft] = useState(null);
  const labelContainerRef = useRef(null);
  const labelDragStateRef = useRef(null);

  const defaultLabelLeft = (dimensions.width * scaleFactor) + 8;
  const defaultLabelTop = 0;
  const currentTextPosition = data?.textPosition;
  const labelLeft = typeof currentTextPosition?.x === 'number' ? currentTextPosition.x : defaultLabelLeft;
  const labelTop = typeof currentTextPosition?.y === 'number' ? currentTextPosition.y : defaultLabelTop;
  const effectiveLabelLeft = labelPositionDraft?.x ?? labelLeft;
  const effectiveLabelTop = labelPositionDraft?.y ?? labelTop;
  const distributionBoardPreviewVisible = attributes.showDistributionBoardPreview?.value === true;
  const distributionBoardPreviewScaleRaw = Number(attributes.distributionBoardPreviewScale?.value);
  const distributionBoardPreviewScale = Number.isFinite(distributionBoardPreviewScaleRaw) && distributionBoardPreviewScaleRaw > 0
    ? distributionBoardPreviewScaleRaw
    : 1;
  const previewPosition = attributes.distributionBoardPreviewPosition?.value;
  const previewDefaultLeft = 0;
  const previewDefaultTop = (dimensions.height * scaleFactor) + 24;
  const previewLeft = typeof previewPosition?.x === 'number' ? previewPosition.x : previewDefaultLeft;
  const previewTop = typeof previewPosition?.y === 'number' ? previewPosition.y : previewDefaultTop;
  const [previewPositionDraft, setPreviewPositionDraft] = useState(null);
  const [isPreviewDragging, setIsPreviewDragging] = useState(false);
  const previewContainerRef = useRef(null);
  const previewDragStateRef = useRef(null);
  const effectivePreviewLeft = previewPositionDraft?.x ?? previewLeft;
  const effectivePreviewTop = previewPositionDraft?.y ?? previewTop;
  const distributionBoardHeaders = Array.isArray(attributes.headerData?.value) ? attributes.headerData.value : [];
  const distributionBoardRows = Array.isArray(attributes.rowData?.value) ? attributes.rowData.value : [];
  const distributionBoardColumnWidths = Array.isArray(attributes.columnWidths?.value) ? attributes.columnWidths.value : [];

  const getLabelMovementBounds = useCallback(() => {
    const rfNode = reactFlowInstance?.getNode?.(id);
    const nodeWidth = rfNode?.width ?? (dimensions.width * scaleFactor);
    const nodeHeight = rfNode?.height ?? (dimensions.height * scaleFactor);
    return {
      minX: -MAX_LABEL_DISTANCE,
      maxX: (nodeWidth || 0) + MAX_LABEL_DISTANCE,
      minY: -MAX_LABEL_DISTANCE,
      maxY: (nodeHeight || 0) + MAX_LABEL_DISTANCE,
    };
  }, [reactFlowInstance, id, dimensions.width, dimensions.height, scaleFactor]);

  const updateTextPosition = useCallback(
    (nextX, nextY) => {
      const roundedX = Math.round(nextX * 100) / 100;
      const roundedY = Math.round(nextY * 100) / 100;
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id !== id) {
            return node;
          }
          const previous = node?.data?.textPosition;
          if (previous && Math.abs(previous.x - roundedX) < 0.5 && Math.abs(previous.y - roundedY) < 0.5) {
            return node;
          }
          const nextData = { ...(node.data || {}) };
          nextData.textPosition = { x: roundedX, y: roundedY };
          return { ...node, data: nextData };
        })
      );
    },
    [id, setNodes],
  );

  const updateDistributionBoardPreviewPosition = useCallback(
    (nextX, nextY) => {
      const roundedX = Math.round(nextX * 100) / 100;
      const roundedY = Math.round(nextY * 100) / 100;
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id !== id) {
            return node;
          }
          const existingAttributes = node?.data?.attributes || {};
          const previous = existingAttributes.distributionBoardPreviewPosition?.value;
          if (previous && Math.abs(previous.x - roundedX) < 0.5 && Math.abs(previous.y - roundedY) < 0.5) {
            return node;
          }
          return {
            ...node,
            data: {
              ...(node.data || {}),
              attributes: {
                ...existingAttributes,
                distributionBoardPreviewPosition: {
                  ...(existingAttributes.distributionBoardPreviewPosition || {}),
                  value: { x: roundedX, y: roundedY },
                },
              },
            },
          };
        })
      );
    },
    [id, setNodes],
  );

  const handleLabelPointerDown = useCallback(
    (event) => {
      if (!isLabelPositionEditing) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {}
      const draftStart = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        initialX: effectiveLabelLeft,
        initialY: effectiveLabelTop,
        lastReportedX: effectiveLabelLeft,
        lastReportedY: effectiveLabelTop,
      };
      labelDragStateRef.current = draftStart;
      setIsLabelDragging(true);
      setLabelPositionDraft({ x: draftStart.initialX, y: draftStart.initialY });
      const rfNode = reactFlowInstance?.getNode?.(id);
      const nodeWidth = rfNode?.width ?? (dimensions.width * scaleFactor);
      const nodeHeight = rfNode?.height ?? (dimensions.height * scaleFactor);
    },
    [isLabelPositionEditing, labelLeft, labelTop, setIsLabelDragging, reactFlowInstance, id, dimensions.width, dimensions.height, scaleFactor],
  );

  const handleLabelPointerMove = useCallback(
    (event) => {
      if (!isLabelPositionEditing) {
        return;
      }
      const dragState = labelDragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const dx = event.clientX - dragState.startX;
      const dy = event.clientY - dragState.startY;
      const bounds = getLabelMovementBounds();
      let nextX = dragState.initialX + dx;
      let nextY = dragState.initialY + dy;
      if (nextX < bounds.minX) nextX = bounds.minX;
      if (nextX > bounds.maxX) nextX = bounds.maxX;
      if (nextY < bounds.minY) nextY = bounds.minY;
      if (nextY > bounds.maxY) nextY = bounds.maxY;
      dragState.lastReportedX = nextX;
      dragState.lastReportedY = nextY;
      labelDragStateRef.current = dragState;
      setLabelPositionDraft({ x: nextX, y: nextY });
    },
    [isLabelPositionEditing, getLabelMovementBounds, id],
  );

  const handleLabelPointerUp = useCallback(
    (event) => {
      const dragState = labelDragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {}
      labelDragStateRef.current = null;
      if (typeof dragState.lastReportedX === 'number' && typeof dragState.lastReportedY === 'number') {
        updateTextPosition(dragState.lastReportedX, dragState.lastReportedY);
      }
      setLabelPositionDraft(null);
      setIsLabelDragging(false);
    },
    [setIsLabelDragging, id, labelLeft, labelTop, updateTextPosition],
  );

  const handlePreviewPointerDown = useCallback(
    (event) => {
      if (!isDistributionBoardPreviewEditing) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {}
      const draftStart = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        initialX: effectivePreviewLeft,
        initialY: effectivePreviewTop,
        lastReportedX: effectivePreviewLeft,
        lastReportedY: effectivePreviewTop,
      };
      previewDragStateRef.current = draftStart;
      setIsPreviewDragging(true);
      setPreviewPositionDraft({ x: draftStart.initialX, y: draftStart.initialY });
    },
    [effectivePreviewLeft, effectivePreviewTop, isDistributionBoardPreviewEditing],
  );

  const handlePreviewPointerMove = useCallback(
    (event) => {
      if (!isDistributionBoardPreviewEditing) {
        return;
      }
      const dragState = previewDragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const nextX = dragState.initialX + (event.clientX - dragState.startX);
      const nextY = dragState.initialY + (event.clientY - dragState.startY);
      dragState.lastReportedX = nextX;
      dragState.lastReportedY = nextY;
      previewDragStateRef.current = dragState;
      setPreviewPositionDraft({ x: nextX, y: nextY });
    },
    [isDistributionBoardPreviewEditing],
  );

  const handlePreviewPointerUp = useCallback(
    (event) => {
      const dragState = previewDragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {}
      previewDragStateRef.current = null;
      if (typeof dragState.lastReportedX === 'number' && typeof dragState.lastReportedY === 'number') {
        updateDistributionBoardPreviewPosition(dragState.lastReportedX, dragState.lastReportedY);
      }
      setPreviewPositionDraft(null);
      setIsPreviewDragging(false);
    },
    [updateDistributionBoardPreviewPosition],
  );

  useEffect(() => {
    if (isLabelPositionEditing && (typeof currentTextPosition?.x !== 'number' || typeof currentTextPosition?.y !== 'number')) {
      updateTextPosition(defaultLabelLeft, defaultLabelTop);
    }
  }, [isLabelPositionEditing, currentTextPosition, defaultLabelLeft, defaultLabelTop, updateTextPosition]);

  useEffect(() => {
    if (!isLabelPositionEditing && labelDragStateRef.current) {
      labelDragStateRef.current = null;
      if (isLabelDragging) {
        setIsLabelDragging(false);
      }
      setLabelPositionDraft(null);
    }
  }, [isLabelPositionEditing, isLabelDragging, setIsLabelDragging, setLabelPositionDraft]);

  useEffect(() => {
    if (!isDistributionBoardPreviewEditing && previewDragStateRef.current) {
      previewDragStateRef.current = null;
      if (isPreviewDragging) {
        setIsPreviewDragging(false);
      }
      setPreviewPositionDraft(null);
    }
  }, [isDistributionBoardPreviewEditing, isPreviewDragging]);

  const labelMovementBounds = useMemo(() => {
    if (!isLabelPositionEditing) {
      return null;
    }
    return getLabelMovementBounds();
  }, [isLabelPositionEditing, getLabelMovementBounds]);

  const labelContainerClassName = [
    'table-container',
    isLabelPositionEditing ? 'table-container--editable' : '',
    isLabelDragging ? 'table-container--editable-dragging' : '',
  ].filter(Boolean).join(' ');

  let angleOffset = VERTICAL_ORIENTATION;
  if (data?.isRotable) {
    angleOffset = typeof data.angle === 'number' ? data.angle : VERTICAL_ORIENTATION;
  }

  // Ensure edges re-anchor when angle changes
  useEffect(() => {
    try { reactFlowInstance?.updateNodeInternals?.(id); } catch {}
    // Nudge edges so RF recomputes endpoints from new handle anchors
    try { reactFlowInstance?.setEdges?.((eds) => (Array.isArray(eds) ? eds.map((e) => ({ ...e })) : eds)); } catch {}
  }, [id, data?.angle, reactFlowInstance]);

  // Ensure valid handles
  const highlightKeys = useSelector((state) => state.view.highlightHandles) || [];
  const handlesHidden = useSelector((state) => !!state.view.handlesHidden);
  const connectionAssist = useSelector((state) => state.view.connectionAssist) || {};
  const renderHandles = () => {
    const expectedRoles = connectionAssist?.active
      ? (Array.isArray(connectionAssist.expectedRoles) && connectionAssist.expectedRoles.length > 0
        ? connectionAssist.expectedRoles
        : (connectionAssist.expectedRole ? [connectionAssist.expectedRole] : []))
      : [];
    const hoveredNodeId = connectionAssist?.hoveredNodeId || null;
    const hoveredHandleId = connectionAssist?.hoveredHandleId || null;
    const shouldRevealHandle = (role) => handlesHidden && expectedRoles.includes(role);
    const isHoveredValidHandle = (handleId) => hoveredNodeId === id && hoveredHandleId === handleId;
    const getHandleClassName = (role, handleId) => {
      const classNames = ['node-handle'];
      classNames.push(`handle-node-${toHandleClassToken(id, 'node')}`);
      classNames.push(`handle-id-${toHandleClassToken(handleId || role, role || 'handle')}`);
      if (shouldRevealHandle(role)) {
        classNames.push('connection-assist-visible');
      }
      return classNames.join(' ');
    };

    const handleDoubleClick = (hId) => () => {
      const rfNodes = reactFlowInstance.getNodes ? reactFlowInstance.getNodes() : [];
      const selfRF = rfNodes.find(n => n.id === id);
      const selfWidth = selfRF?.width || 0;
      const anchorX = (selfRF?.position?.x || 0) + Math.floor(selfWidth / 2);

      const childIds = edges
        .filter((e) => e.source === id && (hId ? e.sourceHandle === hId : true))
        .map((e) => e.target);
      if (childIds.length === 0) return;
      const gap = Math.max(5, Math.min(200, Number(gridGapPx) || 25));
      const snappedX = Math.round(anchorX / gap) * gap;

      const widthById = new Map();
      rfNodes.forEach((n) => { if (n && n.id) widthById.set(n.id, n.width || 0); });

      setNodes((nds) => nds.map((n) => (
        childIds.includes(n.id)
          ? { ...n, position: { ...n.position, x: snappedX - Math.floor((widthById.get(n.id) || 0) / 2) } }
          : n
      )));
    };
    // Map handle side depending on rotation: when horizontal, Top->Left, Bottom->Right
    const mapPos = (pos) => {
      const isVertical = angleOffset === VERTICAL_ORIENTATION;
      if (isVertical) return pos;
      switch (pos) {
        case Position.Top: return Position.Left;
        case Position.Bottom: return Position.Right;
        case Position.Left: return Position.Top;
        case Position.Right: return Position.Bottom;
        default: return pos;
      }
    };
    
    if (Array.isArray(data?.handles) && data.handles.length > 0) {
      const rfNode = reactFlowInstance?.getNode?.(id);
      const nodeWidthPx = rfNode?.width ?? (dimensions.width * scaleFactor);
      const nodeHeightPx = rfNode?.height ?? (dimensions.height * scaleFactor);
      const nodePosX = currentNode?.position?.x ?? 0;
      const nodePosY = currentNode?.position?.y ?? 0;
      const isThreeWindingNode = (data?.baseAlias || data?.alias) === THREE_WINDING_BASE_ALIAS;
      const threeWindingSourceHandles = isThreeWindingNode
        ? data.handles.filter((handle) => (handle?.type || '').toLowerCase() === 'source')
        : [];
      const isSwitchTransferNode = (data?.baseAlias || data?.alias) === SWITCH_TRANSFER_BASE_ALIAS;
      const switchTransferTargetHandles = isSwitchTransferNode
        ? data.handles.filter((handle) => (handle?.type || '').toLowerCase() === 'target')
        : [];
      const switchTransferSourceHandles = isSwitchTransferNode
        ? data.handles.filter((handle) => (handle?.type || '').toLowerCase() === 'source')
        : [];
      const gridSpacingPx = Math.max(5, Math.min(200, Number(gridGapPx) || 25));

      return data.handles.map((handleConfig, index) => {
        const mapped = mapPos(handleConfig.position);
        let style = handleConfig?.style ? { ...handleConfig.style } : {};
        if (mapped !== handleConfig.position) {
          style = { ...style };
          if (handleConfig.position === Position.Top || handleConfig.position === Position.Bottom) {
            const leftVal = style.left;
            delete style.left; delete style.right;
            if (typeof leftVal !== 'undefined') style.top = leftVal;
          } else if (handleConfig.position === Position.Left || handleConfig.position === Position.Right) {
            const topVal = style.top;
            delete style.top; delete style.bottom;
            if (typeof topVal !== 'undefined') style.left = topVal;
          }
        }

        let customPositioned = false;
        const applyFractionPlacement = (handlesList, resolveFraction, requiredType) => {
          if (customPositioned) return;
          if (!Array.isArray(handlesList) || handlesList.length < 1) return;
          const normalizedType = (handleConfig?.type || '').toLowerCase();
          if (normalizedType !== requiredType) return;
          const handleIndex = handlesList.indexOf(handleConfig);
          if (handleIndex < 0) return;
          const fraction = Number(resolveFraction(handleIndex, handlesList.length));
          if (!Number.isFinite(fraction)) return;
          const clampedFraction = Math.max(0, Math.min(1, fraction));
          if (mapped === Position.Top || mapped === Position.Bottom) {
            const offsetPx = (nodeWidthPx || 0) * clampedFraction;
            style = {
              position: 'absolute',
              left: `${offsetPx}px`,
              right: 'auto',
              transform: 'translate(-50%, 0)',
              ...(mapped === Position.Bottom
                ? { bottom: '-5px', top: 'auto' }
                : { top: '-5px', bottom: 'auto' }),
            };
            customPositioned = true;
          } else if (mapped === Position.Left || mapped === Position.Right) {
            const offsetPx = (nodeHeightPx || 0) * clampedFraction;
            style = {
              position: 'absolute',
              top: `${offsetPx}px`,
              bottom: 'auto',
              transform: 'translate(0, -50%)',
            };
            if (mapped === Position.Right) {
              style.right = '-5px';
              style.left = 'auto';
            } else {
              style.left = '-5px';
              style.right = 'auto';
            }
            customPositioned = true;
          }
        };

        applyFractionPlacement(threeWindingSourceHandles, resolveThreeWindingHandleFraction, 'source');
        applyFractionPlacement(
          switchTransferTargetHandles,
          (handleIndex, handleCount) => resolveSwitchTransferHandleFraction('target', handleIndex, handleCount),
          'target'
        );
        applyFractionPlacement(
          switchTransferSourceHandles,
          (handleIndex, handleCount) => resolveSwitchTransferHandleFraction('source', handleIndex, handleCount),
          'source'
        );

        if (!customPositioned && handleConfig.gridAlign) {
          const count = Math.max(1, Number(handleConfig.gridAlign.count) || 1);
          const rawIndex = Number(handleConfig.gridAlign.index);
          const indexClamped = Number.isFinite(rawIndex) ? Math.min(count - 1, Math.max(0, rawIndex)) : Math.min(count - 1, Math.max(0, index));
          const fraction = count <= 1 ? 0.5 : indexClamped / (count - 1);

          const spacing = gridSpacingPx;
          if (mapped === Position.Top || mapped === Position.Bottom) {
            const width = nodeWidthPx || 0;
            const baseOffset = fraction * width;
            const snappedAbs = Math.round((nodePosX + baseOffset) / spacing) * spacing;
            let offsetPx = snappedAbs - nodePosX;
            offsetPx = Math.max(0, Math.min(width, offsetPx));
            style.position = 'absolute';
            style.left = `${offsetPx}px`;
            style.right = 'auto';
            style.transform = 'translate(-50%, 0)';
            if (mapped === Position.Bottom) {
              style.bottom = style.bottom ?? '-5px';
              style.top = 'auto';
            } else {
              style.top = style.top ?? '-5px';
              style.bottom = 'auto';
            }
          } else if (mapped === Position.Left || mapped === Position.Right) {
            const height = nodeHeightPx || 0;
            const baseOffset = fraction * height;
            const snappedAbs = Math.round((nodePosY + baseOffset) / spacing) * spacing;
            let offsetPx = snappedAbs - nodePosY;
            offsetPx = Math.max(0, Math.min(height, offsetPx));
            style.position = 'absolute';
            style.top = `${offsetPx}px`;
            style.bottom = 'auto';
            style.transform = 'translate(0, -50%)';
            if (mapped === Position.Right) {
              style.right = style.right ?? '-5px';
              style.left = 'auto';
            } else {
              style.left = style.left ?? '-5px';
              style.right = 'auto';
            }
          }
        }

        const isHi = highlightKeys.includes(`${id}|${handleConfig.id}`);
        const handleRole = (handleConfig.type || '').toLowerCase();
        const isValidHover = isHoveredValidHandle(handleConfig.id);
        return (
          <Handle
            key={`${handleConfig.id || index}-${mapped}`}
            id={handleConfig.id}
            type={handleConfig.type}
            position={mapped}
            className={getHandleClassName(handleRole, handleConfig.id)}
            style={{
              ...style,
              backgroundColor: isValidHover ? '#52C41A' : (isHi ? '#52C41A' : undefined),
              borderColor: isValidHover ? '#25d618' : style.borderColor,
            }}
            onDoubleClick={handleConfig.type === 'source' ? handleDoubleClick(handleConfig.id) : undefined}
          />
        );
      });
    }
    // Default handles: Top/Bottom in vertical; Left/Right in horizontal
    const targetPosition = angleOffset === VERTICAL_ORIENTATION ? Position.Top : Position.Left;
    const sourcePosition = angleOffset === VERTICAL_ORIENTATION ? Position.Bottom : Position.Right;
    const hiTarget = highlightKeys.includes(`${id}|target`);
    const hiSource = highlightKeys.includes(`${id}|source`);
    const validTargetHover = isHoveredValidHandle('target');
    const validSourceHover = isHoveredValidHandle('source');
    return (
      <>
        <Handle
          type="target"
          id="target"
          position={targetPosition}
          className={getHandleClassName('target', 'target')}
          style={{
            backgroundColor: validTargetHover ? '#52C41A' : (hiTarget ? '#52C41A' : undefined),
            borderColor: validTargetHover ? '#25d618' : undefined,
          }}
        />
        <Handle
          type="source"
          id="source"
          position={sourcePosition}
          className={getHandleClassName('source', 'source')}
          onDoubleClick={handleDoubleClick(null)}
          style={{
            backgroundColor: validSourceHover ? '#52C41A' : (hiSource ? '#52C41A' : undefined),
            borderColor: validSourceHover ? '#25d618' : undefined,
          }}
        />
      </>
    );
  };

  let imageSrc = data?.svgSrc;

  if (data?.variants && Array.isArray(data.variants)) {
    const selectedVariant =
      data.variants.find((variant) => variant.selected) || data.variants[0];
    if (selectedVariant && selectedVariant.diagramSvgSrc) {
      imageSrc = selectedVariant.diagramSvgSrc;
    }
  }

  if ((data?.baseAlias || data?.alias) === THREE_WINDING_BASE_ALIAS) {
    imageSrc = resolveThreeWindingImage(globalScaleCm);
  }
  if ((data?.baseAlias || data?.alias) === SWITCH_TRANSFER_BASE_ALIAS) {
    imageSrc = resolveSwitchTransferImage(globalScaleCm);
  }
  if ((data?.baseAlias || data?.alias) === SWITCH_ALIAS) {
    imageSrc = resolveSwitchImage(globalScaleCm);
  }
  if ((data?.baseAlias || data?.alias) === INTERRUPTOR_MT_AT_A_ALIAS) {
    imageSrc = resolveInterruptorMtAtAImage(globalScaleCm);
  }
  if ((data?.baseAlias || data?.alias) === UTILITY_B_ALIAS) {
    imageSrc = resolveUtilityBImage(globalScaleCm);
  }

  return (
    <div
      className={`node-wrapper ${isSelected ? 'selected-node' : ''}`}
      data-node-color={tintColor || undefined}
      style={wrapperStyle}
    >
      {renderHandles()}

      <div
        className="rotate-container"
        style={{ position: 'relative', transform: `rotate(${angleOffset}deg)` }}
      >
        <InlineOrImg imageSrc={imageSrc} width={dimensions.width * scaleFactor} height={dimensions.height * scaleFactor} />
      </div>

      {isLabelPositionEditing && labelMovementBounds ? (
        <div
          className="label-move-bounds"
          style={{
            position: 'absolute',
            left: labelMovementBounds.minX,
            top: labelMovementBounds.minY,
            width: labelMovementBounds.maxX - labelMovementBounds.minX,
            height: labelMovementBounds.maxY - labelMovementBounds.minY,
          }}
        />
      ) : null}

      {attributeRows.length > 0 && (
        <div
          ref={labelContainerRef}
          className={labelContainerClassName}
          onPointerDown={handleLabelPointerDown}
          onPointerMove={handleLabelPointerMove}
          onPointerUp={handleLabelPointerUp}
          onPointerCancel={handleLabelPointerUp}
          style={{
            position: 'absolute',
            top: effectiveLabelTop,
            left: effectiveLabelLeft,
            fontSize: `${labelFontPx}px`,
            transform: 'none',
            cursor: isLabelPositionEditing ? (isLabelDragging ? 'grabbing' : 'grab') : undefined,
            userSelect: isLabelPositionEditing ? 'none' : undefined,
          }}
        >
          {attributeRows}
        </div>
      )}

      {distributionBoardPreviewVisible && distributionBoardHeaders.length > 0 ? (
        <div
          ref={previewContainerRef}
          className={`distribution-board-preview${isDistributionBoardPreviewEditing ? ' distribution-board-preview--editable' : ''}${isPreviewDragging ? ' distribution-board-preview--dragging' : ''}`}
          onPointerDown={handlePreviewPointerDown}
          onPointerMove={handlePreviewPointerMove}
          onPointerUp={handlePreviewPointerUp}
          onPointerCancel={handlePreviewPointerUp}
          style={{
            left: effectivePreviewLeft,
            top: effectivePreviewTop,
            cursor: isDistributionBoardPreviewEditing ? (isPreviewDragging ? 'grabbing' : 'grab') : 'default',
            userSelect: isDistributionBoardPreviewEditing ? 'none' : undefined,
          }}
        >
          <DistributionBoardPreview
            headers={distributionBoardHeaders}
            rows={distributionBoardRows}
            columnWidths={distributionBoardColumnWidths}
            scale={distributionBoardPreviewScale}
          />
        </div>
      ) : null}
    </div>
  );
};

export default CustomNode;

function DistributionBoardPreview({ headers, rows, columnWidths, scale }) {
  const normalizedScale = Number.isFinite(Number(scale)) && Number(scale) > 0 ? Number(scale) : 1;
  const normalizedWidths = headers.map((header, index) => {
    const width = Number(columnWidths[index]);
    if (Number.isFinite(width) && width > 0) {
      return Math.max(96, Math.round(width * normalizedScale));
    }
    if (header === 'Circuito') return Math.round(110 * normalizedScale);
    if (header === 'Descripción de Carga') return Math.round(240 * normalizedScale);
    return Math.round(140 * normalizedScale);
  });
  const headerFontPx = Math.max(11, Math.round(12 * normalizedScale));
  const cellFontPx = Math.max(10, Math.round(11 * normalizedScale));
  const rowPaddingPx = Math.max(6, Math.round(8 * normalizedScale));

  return (
    <div className="distribution-board-preview__scroll">
      <table className="distribution-board-preview__table">
        <colgroup>
          {headers.map((_, index) => (
            <col key={`preview-col-${index}`} style={{ width: normalizedWidths[index], minWidth: normalizedWidths[index] }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th key={`preview-header-${index}`} style={{ fontSize: `${headerFontPx}px`, padding: `${rowPaddingPx}px` }}>
                <div className="distribution-board-preview__text">{header}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`preview-row-${rowIndex}`}>
              {headers.map((header, columnIndex) => (
                <td key={`preview-cell-${rowIndex}-${columnIndex}`} style={{ fontSize: `${cellFontPx}px`, padding: `${rowPaddingPx}px` }}>
                  <div className="distribution-board-preview__text">{row?.[header] ?? ''}</div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InlineOrImg({ imageSrc, width, height }) {
  const [svgMarkup, setSvgMarkup] = useState(null);
  const scopeIdRef = useRef(null);
  const containerStyle = useMemo(
    () => ({
      width,
      height,
    }),
    [width, height],
  );

  useEffect(() => {
    let active = true;
    setSvgMarkup(null);
    if (!imageSrc) return;
    const isSvg = /\.svg(\?|#|$)/i.test(imageSrc) || /symbol-assets/i.test(imageSrc);
    if (!isSvg) return; // render as <img>
    (async () => {
      try {
        const abs = new URL(imageSrc, document.baseURI).toString();
        const res = await fetch(abs, { credentials: 'same-origin' });
        let text = await res.text();
        // Remove XML prolog which can break HTML parsing
        text = text.replace(/^<\?xml[^>]*\?>\s*/i, '');
        const scoped = scopeInlineSvg(text, scopeIdRef);
        if (active) setSvgMarkup(scoped);
      } catch (e) {
        if (active) setSvgMarkup(null);
      }
    })();
    return () => { active = false; };
  }, [imageSrc]);

  if (svgMarkup) {
    return (
      <div
        className="inline-svg-container"
        style={containerStyle}
        data-inline-width={typeof width === 'number' ? width : undefined}
        data-inline-height={typeof height === 'number' ? height : undefined}
        dangerouslySetInnerHTML={{ __html: svgMarkup }}
      />
    );
  }
  let resolved = imageSrc;
  try {
    if (imageSrc) resolved = new URL(imageSrc, document.baseURI).toString();
  } catch (e) {}
  return (
    <img
      crossOrigin="anonymous"
      src={resolved}
      alt="Node"
      style={{ width, height }}
    />
  );
}

function scopeInlineSvg(markup, scopeRef) {
  try {
    if (!markup) return markup;
    if (!scopeRef.current) {
      scopeRef.current = `svg-scope-${Math.random().toString(36).slice(2, 10)}`;
    }
    const scopeToken = scopeRef.current;
    const parser = new DOMParser();
    const doc = parser.parseFromString(markup, 'image/svg+xml');
    const svgEl = doc.documentElement;
    if (!svgEl || svgEl.nodeName.toLowerCase() !== 'svg') {
      return markup;
    }

    svgEl.setAttribute('data-inline-scope', scopeToken);
    const viewBox = svgEl.getAttribute('viewBox');
    if (viewBox && !svgEl.hasAttribute('width') && !svgEl.hasAttribute('height')) {
      const parts = viewBox.split(/[\s,]+/).map((val) => Number(val));
      if (parts.length === 4) {
        const [, , vbWidth, vbHeight] = parts;
        if (Number.isFinite(vbWidth) && Number.isFinite(vbHeight) && vbWidth > 0 && vbHeight > 0) {
          svgEl.setAttribute('width', '100%');
          svgEl.setAttribute('height', '100%');
          svgEl.style.aspectRatio = `${vbWidth} / ${vbHeight}`;
          svgEl.style.width = '100%';
          svgEl.style.height = '100%';
        }
      }
    }

    const styleEls = svgEl.querySelectorAll('style');
    styleEls.forEach((styleEl) => {
      const raw = styleEl.textContent || '';
      if (!raw.trim()) return;
      const cleaned = raw.replace(/<!\[CDATA\[|\]\]>/g, '');
      const scoped = cleaned
        .split('}')
        .map((block) => {
          const parts = block.split('{');
          if (parts.length < 2) return '';
          const selectorPart = parts.slice(0, -1).join('{').trim();
          const declPart = parts[parts.length - 1];
          if (!selectorPart) return '';
          if (selectorPart.startsWith('@')) {
            return `${selectorPart}{${declPart}}`;
          }
          const scopedSelectors = selectorPart
            .split(',')
            .map((sel) => sel.trim())
            .filter(Boolean)
            .map((sel) => {
              if (sel.startsWith(`[data-inline-scope="${scopeToken}"]`)) return sel;
              if (sel.startsWith('@')) return sel;
              return `[data-inline-scope="${scopeToken}"] ${sel}`;
            })
            .join(', ');
          return scopedSelectors ? `${scopedSelectors}{${declPart}}` : '';
        })
        .filter(Boolean)
        .join(' ');
      styleEl.textContent = scoped;
    });

    const serializer = new XMLSerializer();
    return serializer.serializeToString(svgEl);
  } catch (err) {
    return markup;
  }
}
